import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Format date to Indonesian locale
export function formatDate(dateString, options = {}) {
  const date = new Date(dateString);
  const defaultOptions = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...options
  };
  return date.toLocaleDateString('id-ID', defaultOptions);
}

// Format time
export function formatTime(dateString) {
  const date = new Date(dateString);
  return date.toLocaleTimeString('id-ID', {
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Format datetime
export function formatDateTime(dateString) {
  const date = new Date(dateString);
  return date.toLocaleString('id-ID', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Get status badge color class
export function getStatusColor(status) {
  const colors = {
    hadir_tepat_waktu: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    hadir_terlambat: 'bg-amber-100 text-amber-800 border-amber-200',
    mangkir: 'bg-red-100 text-red-800 border-red-200',
    izin: 'bg-blue-100 text-blue-800 border-blue-200',
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    disetujui: 'bg-green-100 text-green-800 border-green-200',
    ditolak: 'bg-red-100 text-red-800 border-red-200'
  };
  return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
}

// Get status label
export function getStatusLabel(status) {
  const labels = {
    hadir_tepat_waktu: 'Hadir Tepat Waktu',
    hadir_terlambat: 'Hadir Terlambat',
    mangkir: 'Mangkir',
    izin: 'Izin',
    pending: 'Menunggu',
    disetujui: 'Disetujui',
    ditolak: 'Ditolak'
  };
  return labels[status] || status;
}

// Get role label
export function getRoleLabel(role) {
  const labels = {
    guru: 'Guru',
    kepala_sekolah: 'Kepala Sekolah',
    admin_dinas: 'Admin Dinas',
    pimpinan: 'Pimpinan',
    editor: 'Editor',
    pengawas: 'Pengawas'
  };
  return labels[role] || role;
}

// Calculate distance between coordinates (Haversine)
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000; // Earth radius in meters
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLambda = ((lon2 - lon1) * Math.PI) / 180;

  const a = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
    Math.cos(phi1) * Math.cos(phi2) *
    Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

// Format distance
export function formatDistance(meters) {
  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }
  return `${(meters / 1000).toFixed(2)} km`;
}

// Get month name in Indonesian
export function getMonthName(monthNumber) {
  const months = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
  ];
  return months[monthNumber - 1] || '';
}

// Generate score color
export function getScoreColor(score) {
  if (score >= 90) return 'text-emerald-600';
  if (score >= 75) return 'text-blue-600';
  if (score >= 60) return 'text-amber-600';
  return 'text-red-600';
}

// Generate score badge
export function getScoreBadge(score) {
  if (score >= 90) return { label: 'Sangat Baik', color: 'bg-emerald-100 text-emerald-800' };
  if (score >= 75) return { label: 'Baik', color: 'bg-blue-100 text-blue-800' };
  if (score >= 60) return { label: 'Cukup', color: 'bg-amber-100 text-amber-800' };
  return { label: 'Kurang', color: 'bg-red-100 text-red-800' };
}

// Compress image for storage
export async function compressImage(base64, maxWidth = 800, quality = 0.7) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.src = base64;
  });
}

// Check if date is today
export function isToday(dateString) {
  const date = new Date(dateString);
  const today = new Date();
  return date.toDateString() === today.toDateString();
}

// Get greeting based on time
export function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 11) return 'Selamat Pagi';
  if (hour < 15) return 'Selamat Siang';
  if (hour < 18) return 'Selamat Sore';
  return 'Selamat Malam';
}
